import { Ref } from 'react';

import { formatScoreFormatTitle } from '../../helpers';
import { Match } from '../../types';

import { SimplifiedMatchScoreRow } from './SimplifiedMatchScoreRow';

interface SimplifiedScoreSheetPrintProps {
	matches: Match[] | Match;
	teamOneTitle: string;
	teamTwoTitle: string;
	innerRef: Ref<HTMLDivElement>;
	sheetSize?: 'Half Sheets' | 'Full Sheets';
	shouldDisplayPlayerNames?: {
		showHomeLineup: boolean;
		showAwayLineup: boolean;
	};
}

export const SimplifiedScoreSheetPrint = ({
	matches,
	teamOneTitle,
	teamTwoTitle,
	innerRef,
	sheetSize = 'Half Sheets',
	shouldDisplayPlayerNames
}: SimplifiedScoreSheetPrintProps) => {
	return (
		<div ref={innerRef} className="m-0 p-0 print:block hidden">
			{/* NOTE: temporary sorting matches by court title, we need better sorting logic. see ticket https://pickleball-product-team.atlassian.net/browse/PW-3969 */}
			{[matches]
				.flat()
				.sort((a, b) => {
					const getCourtNumber = (title: string | null | undefined) => {
						const num = title ? parseInt(title, 10) : NaN;
						return isNaN(num) ? null : num;
					};

					const numA = getCourtNumber(a.courtTitle);
					const numB = getCourtNumber(b.courtTitle);

					// Numbers first, then alphabetical
					if (numA !== null && numB !== null) return numA - numB;
					if (numA !== null) return -1;
					if (numB !== null) return 1;
					return (a.courtTitle || '').localeCompare(b.courtTitle || '');
				})
				.map((match, index) => {
					const isDoubles = match.formatTitle.toLowerCase().includes('doubles');
					const teamOnePlayers = isDoubles
						? [`${match.teamTwoPlayerOneName} / ${match.teamTwoPlayerTwoName}`]
						: [match.teamTwoPlayerOneName || ' '];

					const teamTwoPlayers = isDoubles
						? [`${match.teamOnePlayerOneName} / ${match.teamOnePlayerTwoName}`]
						: [match.teamOnePlayerOneName || ' '];

					const isNoSetupTeamOneLineup =
						(!match.teamTwoPlayerOneName && !match.teamTwoPlayerTwoName) || shouldDisplayPlayerNames?.showAwayLineup === false;
					const isNoSetupTeamTwoLineup =
						(!match.teamOnePlayerOneName && !match.teamOnePlayerTwoName) || shouldDisplayPlayerNames?.showHomeLineup === false;

					return (
						<div key={match.matchUuid || index} className={`${sheetSize === 'Half Sheets' ? 'print-half-sheet' : 'print-full-sheet'}`}>
							<div className="flex flex-col px-7 pt-2">
								<div className="mt-4 w-full border-t-4 border-dashed border-gray-900 px-2" />
								<div className="flex flex-col gap-2 p-2">
									<p className="font-bold tracking-wider">
										Match: <span className="font-normal">{match.roundText}</span>
									</p>
									<p className="font-bold tracking-wider">
										Court: <span className="font-normal">{match.courtTitle}</span>
									</p>
									<p className="font-bold tracking-wider">
										Format: <span className="font-normal">{formatScoreFormatTitle(match.scoreFormatShorthand)}</span>
									</p>

									<div className="flex w-full flex-col justify-between text-center">
										<SimplifiedMatchScoreRow
											firstTeamTitle={teamTwoTitle}
											secondTeamTitle={teamOneTitle}
											firstTeamPlayers={teamOnePlayers}
											secondTeamPlayers={teamTwoPlayers}
											maxGames={match.scoreFormatMaxGames || 1}
											isNoSetupTeamOneLineup={isNoSetupTeamOneLineup}
											isNoSetupTeamTwoLineup={isNoSetupTeamTwoLineup}
											timeoutCount={match.timeoutCountTeamOneGameOne || 1}
										/>
									</div>
								</div>
								<div className="w-full border-t-4 border-gray-900 px-2" />
							</div>
						</div>
					);
				})}
		</div>
	);
};
